import { applyMiddleware, combineReducers, createStore, compose } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import createSagaMiddleware from "redux-saga";
import storage from "redux-persist/lib/storage";
import thunk from "redux-thunk";

import rootSaga from "./rootSaga";
import { systemReducer } from "./system/reducers";
import { adminHtmlReducer } from "./adminHtml/reducers";

import { inventoryReducer } from "./inventory/reducers";
import { saleReducer } from "./sale/reducers";
import { itemOptionsReducer } from "./itemOptions/reducers";
import { transactionReducer } from "./transaction/reducers";
import { uploadReducer } from "./uploads/reducers";
import { errorAlertsReducer } from "./errorAlerts/reducers";
import { plaidIntegrationReducer } from "./plaidIntegration/reducers";
import { generalLedgerAccountReducer } from "./glAccounts/reducers";
import { supportReducer } from "./support/reducers";
import { zendeskReducer } from "./zendesk/reducers";
import { vendorReducer } from "./vendor/reducers";
import { integrationSyncReducer } from "./integrationSync/reducers";
import { bankAccountReducer } from "./bankAccount/reducers";
import { tourReducer } from "src/store/tour/reducers";
import { userDataLoadReducer } from "./userDataLoad/reducers";

const rootReducer = () => {
  const systemPersistConfig = {
    key: "system",
    storage: storage,
    whitelist: ["user", "loggedIn"],
  };
  return combineReducers({
    system: persistReducer(systemPersistConfig, systemReducer),
    adminHtml: adminHtmlReducer,

    inventory: inventoryReducer,
    sale: saleReducer,
    itemOptions: itemOptionsReducer,
    uploads: uploadReducer,
    transaction: transactionReducer,
    errorAlerts: errorAlertsReducer,
    plaidIntegration: plaidIntegrationReducer,
    glAccounts: generalLedgerAccountReducer,
    support: persistReducer(
      { key: "support", storage, whitelist: ["token", "role"] },
      supportReducer
    ),
    zendesk: zendeskReducer,
    vendor: vendorReducer,
    integrationSync: integrationSyncReducer,
    bankAccount: bankAccountReducer,
    tour: tourReducer,
    userDataLoad: userDataLoadReducer,
  });
};

const rootReducerInstance = rootReducer();

export type AppState = ReturnType<typeof rootReducerInstance>;

const configureStore = async () => {
  const persistConfig = {
    key: "root",
    storage,
    whitelist: [],
  };

  const sagaMiddleware = createSagaMiddleware();

  const persistedReducer = persistReducer(persistConfig, rootReducerInstance);
  const composeEnhancers =
    process.env.NODE_ENV !== "production" &&
    typeof window === "object" &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
          // Specify extension’s options like name, actionsBlacklist, actionsCreators, serialize...
          actionSanitizer: (action) => {
            if (action.type.endsWith("CREATE_ITEMS")) {
              if (action.payload?.length > 5)
                return {
                  ...action,
                  payload: "<<< many elements >>>",
                };
            }

            return action;
          },
          stateSanitizer: ({ ...state }) => {
            for (const k of [
              "sale",
              "inventory",
              "uploads",
              "transaction",
              "errorAlerts",
            ]) {
              if (state[k]?.items?.length > 15) {
                state[k] = {
                  ...state[k],
                  items: "<<< many elements >>>",
                };
              }
            }

            if (
              state.sale &&
              Object.keys(state.sale.itemsById || {}).length > 15
            ) {
              state.sale = {
                ...state.sale,
                itemsById: "<<< many elements >>>" as any,
              };
            }

            if (
              state.transaction &&
              Object.keys(state.transaction.itemsById || {}).length > 15
            ) {
              state.transaction = {
                ...state.transaction,
                itemsById: "<<< many elements >>>" as any,
              };
            }

            if (state.sale?.visible?.length > 15) {
              state.sale = {
                ...state.sale,
                visible: "<<< many elements >>>",
              };
            }

            return state;
          },
        })
      : compose;

  const store = createStore(
    persistedReducer,
    composeEnhancers(applyMiddleware(thunk, sagaMiddleware))
  );

  sagaMiddleware.run(rootSaga);

  const persistor = persistStore(store);

  return { store, persistor };
};

export default configureStore;
